import { useEffect, useRef, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import { toast } from 'react-toastify';
import packageJson from '../../../../package.json';
import VersionUpdateToast from '../VersionUpdateToast';

/**
 * Compares two semantic version strings
 * @param {string} firstVersion - First version string (e.g., "1.10.0")
 * @param {string} newVersion - Second version string (e.g., "2.0.0")
 * @returns {boolean} - Returns true if firstVersion is greater than newVersion, false otherwise
 */
const versionGreaterThan = (firstVersion, newVersion) => {
  const parts1 = firstVersion.split('.').map(Number);
  const parts2 = newVersion.split('.').map(Number);
  const maxLength = Math.max(parts1.length, parts2.length);

  for (let i = 0; i < maxLength; i++) {
    const part1 = parts1[i] || 0;
    const part2 = parts2[i] || 0;

    if (part1 < part2) return false;
    if (part1 > part2) return true;
  }

  return false;
};

const useNewVersionCheck = () => {
  const location = useLocation();
  const refreshTriggeredRef = useRef(false);
  const toastShownRef = useRef(false);
  const previousPathnameRef = useRef(location.pathname);
  const currentVersion = packageJson.version;

  const isNewVersionPresent = useCallback(async () => {
    try {
      const response = await fetch(`/meta.json?v=${+new Date()}`, {
        cache: 'no-store',
      });
      const meta = await response.json();
      const newVersion = meta?.version;
      return versionGreaterThan(newVersion, currentVersion);
    } catch (error) {
      console.error('something went wrong fetching meta.json', error);
      return false;
    }
  }, [currentVersion]);

  useEffect(() => {
    const checkVersionOnNavigation = async () => {
      try {
        if (await isNewVersionPresent()) {
          if (!refreshTriggeredRef.current) {
            refreshTriggeredRef.current = true;

            // Store current route (pathname + search params) to restore after refresh, see useRouteRestore.js
            const currentRoute = location.pathname + location.search;
            sessionStorage.setItem('preRefreshRoute', currentRoute);

            window.location.href = '/';
          }
        }
      } catch (error) {
        console.error('something went wrong fetching meta.json', error);
        return false;
      }
    };
    const pathnameChanged = previousPathnameRef.current !== location.pathname;

    if (pathnameChanged) {
      previousPathnameRef.current = location.pathname;
      checkVersionOnNavigation();
    }
  }, [isNewVersionPresent, location.pathname, location.search]);

  useEffect(() => {
    const checkVersionOnInterval = async () => {
      try {
        if (await isNewVersionPresent()) {
          if (!toastShownRef.current) {
            toastShownRef.current = true;

            const handleRefresh = () => {
              const currentRoute = location.pathname + location.search;
              sessionStorage.setItem('preRefreshRoute', currentRoute);
              window.location.href = '/';
            };

            toast.info(<VersionUpdateToast onRefresh={handleRefresh} />, {
              autoClose: false,
              closeOnClick: true,
              draggable: true,
              onClick: handleRefresh,
            });
          }
        }
      } catch (error) {
        console.error('something went wrong fetching meta.json', error);
      }
    };
    const intervalMinutes =
      Number(process.env.REACT_APP_VERSION_CHECK_INTERVAL) || 5;
    const intervalId = setInterval(
      async () => {
        await checkVersionOnInterval();
      },
      intervalMinutes * 60 * 1000
    );

    return () => clearInterval(intervalId);
  }, [isNewVersionPresent, location.pathname, location.search]);

  return null;
};

export default useNewVersionCheck;
